import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, Trash2, AlertTriangle, Wrench, X, Wifi, Receipt, DollarSign, Package, MapPin } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  getNotifications, 
  getUnreadNotificationCount, 
  markNotificationRead, 
  markAllNotificationsRead,
  deleteNotification 
} from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const dropdownRef = useRef(null);
  const eventSourceRef = useRef(null);
  const navigate = useNavigate();
  const { token } = useAuth();

  // Setup real-time connection
  const connectSSE = useCallback(() => {
    if (!token || eventSourceRef.current) return;

    try {
      const url = `${API_BASE}/api/realtime/events?token=${encodeURIComponent(token)}`;
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.addEventListener('connected', () => {
        setIsConnected(true);
      });

      eventSource.addEventListener('notification', (event) => {
        const notification = JSON.parse(event.data);
        
        // Add to notifications list
        setNotifications(prev => [notification, ...prev.slice(0, 49)]);
        setUnreadCount(prev => prev + 1);
        
        // Show toast
        toast.info(notification.title, {
          description: notification.message,
          duration: 5000
        });
      });

      eventSource.addEventListener('ping', () => {
        setIsConnected(true);
      });

      eventSource.onerror = () => {
        setIsConnected(false);
        eventSource.close();
        eventSourceRef.current = null;
      };
    } catch (error) {
    }
  }, [token]);

  // Cleanup SSE on unmount
  useEffect(() => {
    connectSSE();
    
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [connectSSE]);

  // Polling fallback
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadUnreadCount();
    // Poll less frequently if SSE is connected
    const interval = setInterval(loadUnreadCount, 300000);
    return () => clearInterval(interval);
  }, [isConnected]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const res = await getUnreadNotificationCount();
      setUnreadCount(res.data.count);
    } catch (error) {
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await getNotifications();
      setNotifications(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      loadNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkRead = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
    }
  };

  const handleNotificationClick = (notification) => {
    // Navigate based on notification type
    const navMap = {
      'service_call': '/dashboard/issues',
      'service_call_created': '/dashboard/issues',
      'service_call_updated': '/location-portal/service-calls',
      'issue_created': '/dashboard/issues',
      'issue_resolved': '/dashboard/issues',
      'invoice_paid': '/dashboard/invoices',
      'invoice_overdue': '/dashboard/invoices',
      'collection_recorded': '/dashboard/money-in-out',
      'machine_transferred': '/dashboard/inventory?tab=machines',
      'trial_ending': '/dashboard/subscription',
      'trial_expired': '/dashboard/subscription',
      'payment_failed': '/dashboard/subscription',
      'subscription_renewed': '/dashboard/subscription',
      'subscription_cancelled': '/dashboard/subscription',
    };
    
    const targetPath = notification.action_url || navMap[notification.type] || '/dashboard';
    navigate(targetPath);
    
    if (!notification.is_read) {
      markNotificationRead(notification.id);
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setIsOpen(false);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      'service_call': { icon: Wrench, bg: 'bg-orange-100', color: 'text-orange-600' },
      'service_call_created': { icon: Wrench, bg: 'bg-orange-100', color: 'text-orange-600' },
      'service_call_updated': { icon: Wrench, bg: 'bg-blue-100', color: 'text-blue-600' },
      'issue_created': { icon: AlertTriangle, bg: 'bg-red-100', color: 'text-red-600' },
      'issue_resolved': { icon: Check, bg: 'bg-green-100', color: 'text-green-600' },
      'invoice_paid': { icon: DollarSign, bg: 'bg-green-100', color: 'text-green-600' },
      'invoice_overdue': { icon: Receipt, bg: 'bg-red-100', color: 'text-red-600' },
      'collection_recorded': { icon: DollarSign, bg: 'bg-amber-100', color: 'text-amber-600' },
      'machine_transferred': { icon: Package, bg: 'bg-purple-100', color: 'text-purple-600' },
      'trial_ending': { icon: AlertTriangle, bg: 'bg-amber-100', color: 'text-amber-600' },
      'trial_expired': { icon: AlertTriangle, bg: 'bg-red-100', color: 'text-red-600' },
      'payment_failed': { icon: AlertTriangle, bg: 'bg-red-100', color: 'text-red-600' },
      'subscription_renewed': { icon: DollarSign, bg: 'bg-green-100', color: 'text-green-600' },
      'subscription_cancelled': { icon: AlertTriangle, bg: 'bg-gray-100', color: 'text-gray-600' },
    };
    return iconMap[type] || { icon: Bell, bg: 'bg-gray-100', color: 'text-gray-600' };
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      default: return 'text-green-600 bg-green-50';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleBellClick}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700"
        data-testid="notification-bell"
        title={isConnected ? 'Real-time notifications active' : 'Notifications (polling)'}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {/* Connection indicator dot */}
        <span 
          className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-white dark:border-gray-800 ${
            isConnected ? 'bg-green-500' : 'bg-gray-400'
          }`}
          title={isConnected ? 'Real-time connected' : 'Using polling'}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-[70vh] overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-800 dark:text-white">Notifications</h3>
              {isConnected && (
                <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                  <Wifi className="w-3 h-3" />
                  Live
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllRead}
                  className="text-xs text-teal-600 hover:text-teal-800 dark:text-teal-400"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded">
                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[calc(70vh-60px)]">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const { icon: Icon, bg, color } = getNotificationIcon(notification.type);
                return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-teal-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${bg} ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${!notification.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <span className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">{formatTimeAgo(notification.created_at)}</span>
                        {notification.priority && (
                          <Badge className={`text-xs px-1.5 py-0 ${getPriorityColor(notification.priority)}`}>
                            {notification.priority}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!notification.is_read && (
                        <button
                          onClick={(e) => handleMarkRead(e, notification.id)}
                          className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-teal-600"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleDelete(e, notification.id)}
                        className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-2 border-t border-gray-200 bg-gray-50">
              <Button
                variant="ghost"
                className="w-full text-sm text-teal-600 hover:text-teal-800"
                onClick={() => {
                  navigate('/issues');
                  setIsOpen(false);
                }}
              >
                View all issues
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
